/**
 * Panneau « CE TOUR-CI » — sprint lisibilité (2026-06-10).
 *
 * Bandeau compact sous le bandeau d'indicateurs : les mouvements
 * significatifs du dernier tour (top 5, triés par ampleur), les bascules
 * de palier qualitatif (« Tenable → Préoccupant »), et le contexte du
 * tour (option jouée, événements marquants). C'est la réponse front-only
 * au constat d'audit « un indicateur bouge, le joueur ne sait ni de
 * combien ni pourquoi ».
 *
 * Un clic sur un mouvement ouvre la sheet de détail de l'indicateur.
 */

import { useGame } from "../store/useGame";
import { verdictShiftLabel } from "../engine/turn-report";

const MAX_CHANGES_SHOWN = 5;

export function TurnReportPanel() {
  const report = useGame((s) => s.lastTurnReport);
  const openIndicator = useGame((s) => s.openIndicator);

  if (!report) return null;

  const shown = report.changes.slice(0, MAX_CHANGES_SHOWN);
  const hiddenCount = report.changes.length - shown.length + report.quietCount;
  const { decisionKicker, optionLabel, events } = report.cause;
  const hasDecision = decisionKicker !== null || optionLabel !== null;

  return (
    <section className="turn-report" aria-label="Bilan du dernier tour">
      <span className="turn-report-kicker">CE TOUR-CI</span>

      {/* L'ancre : QUELLE décision a précédé ce bilan (domaine + option choisie).
          Sans elle, les mouvements ci-dessous flottaient sans contexte. */}
      {hasDecision && (
        <span className="turn-report-decision">
          <span className="turn-report-decision-tag">Votre décision</span>
          {decisionKicker && (
            <span className="turn-report-decision-kicker">
              {decisionKicker}
            </span>
          )}
          {optionLabel && (
            <span className="turn-report-decision-option">
              « {optionLabel} »
            </span>
          )}
        </span>
      )}

      {/* Les mouvements — honnêtement cadrés « le pays ce mois-ci » : ils
          mêlent l'effet de la décision, la dérive économique et les événements
          (le moteur ne décompose pas encore par cause). */}
      {shown.length === 0 ? (
        <span className="turn-report-quiet">
          Mois calme — aucun indicateur n'a bougé significativement.
        </span>
      ) : (
        <span className="turn-report-changes">
          <span className="turn-report-changes-tag">Le pays, ce mois-ci</span>
          {shown.map((c) => (
            <button
              key={c.id}
              type="button"
              className="turn-report-chip"
              data-direction={c.direction}
              onClick={() => openIndicator(c.id)}
              title={
                c.verdictShift
                  ? `${c.label} : ${verdictShiftLabel(c.verdictShift)}`
                  : `${c.label} — voir le détail`
              }
            >
              <span className="turn-report-chip-label">{c.label}</span>
              <span className="turn-report-chip-delta">{c.formattedDelta}</span>
              {c.verdictShift && (
                <span className="turn-report-chip-shift">
                  {verdictShiftLabel(c.verdictShift)}
                </span>
              )}
            </button>
          ))}
          {hiddenCount > 0 && (
            <span className="turn-report-more">
              +{hiddenCount} mouvement{hiddenCount > 1 ? "s" : ""} discret
              {hiddenCount > 1 ? "s" : ""}
            </span>
          )}
        </span>
      )}

      {/* Les événements, distingués de la décision : ce que le mois a apporté
          en plus de vos choix. */}
      {events.length > 0 && (
        <span className="turn-report-events">
          <span className="turn-report-events-tag">Faits marquants</span>
          {events.map((e, i) => (
            <span key={i} className="turn-report-event">
              {i > 0 && " · "}
              {e.text}
              {e.satDelta != null && Math.abs(e.satDelta) >= 0.1 && (
                <span
                  className="turn-report-event-impact"
                  data-sign={e.satDelta < 0 ? "neg" : "pos"}
                >
                  {" "}
                  ({e.satDelta > 0 ? "+" : "−"}
                  {Math.abs(e.satDelta).toFixed(1)} moral)
                </span>
              )}
            </span>
          ))}
        </span>
      )}
    </section>
  );
}
